// src/components/LearnerDetail.jsx (Updated with React Router)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  School, 
  Users, 
  Edit3, 
  ArrowLeft,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { LearnerService } from '../../services/learnerService';
import ProfileImage from './ProfileImage';

const LearnerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const location = useLocation();

  const [learner, setLearner] = useState(location.state?.learner || null);
  const [loading, setLoading] = useState(!location.state?.learner);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadLearner();
    }
  }, [id]);

  const loadLearner = async () => {
    try {
      // Only show loading spinner if we don't have initial data from navigation state
      if (!learner) {
        setLoading(true);
      }
      setError(null);
      const data = await LearnerService.getLearner(id);
      setLearner(data);
    } catch (err) {
      setError(err.message || 'Failed to load learner details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const birth = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading learner details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <button
                onClick={() => navigate('/learners')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Learners</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">Error Loading Learner</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!learner) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <button
                onClick={() => navigate('/learners')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Learners</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Learner not found</h3>
            <p className="text-gray-600">The requested learner could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/learners')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Learners</span>
              </button>
              <div className="h-6 border-l border-gray-300"></div>
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-600" />
                <h1 className="text-xl font-semibold text-gray-900">Learner Details</h1>
              </div>
            </div>
            <button
              onClick={() => navigate(`/learners/edit/${learner.id}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit Learner
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Profile Header */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <ProfileImage
                src={learner.profileImageUrl}
                alt={`${learner.firstName} ${learner.surname}`}
                size="2xl"
              />
            </div>
            
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {learner.surname}, {learner.firstName} {learner.secondName}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4 flex-shrink-0" />
                  <span>{learner.gender}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4 flex-shrink-0" />
                  <span>Age {calculateAge(learner.dateOfBirth)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  <span>ID: {learner.identityNumber}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <School className="h-4 w-4 flex-shrink-0" />
                  <span>{learner.schoolName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Information Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <p className="text-gray-900">
                  {learner.surname}, {learner.firstName} {learner.secondName}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <p className="text-gray-900">{learner.gender}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <p className="text-gray-900">{calculateAge(learner.dateOfBirth)} years</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <p className="text-gray-900">{formatDate(learner.dateOfBirth)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Identity Number</label>
                <p className="text-gray-900 font-mono">{learner.identityNumber}</p>
              </div>
              
              {learner.mobileNumber && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.mobileNumber}</p>
                  </div>
                </div>
              )}
              
              {learner.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.email}</p>
                  </div>
                </div>
              )}
              
              {learner.physicalAddress && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-900">{learner.physicalAddress}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Parent/Guardian Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <p className="text-gray-900 capitalize">{learner.parentGuardianType}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <p className="text-gray-900">{learner.parentGuardianName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <p className="text-gray-900">{learner.relationship}</p>
              </div>
              
              {learner.parentMobile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.parentMobile}</p>
                  </div>
                </div>
              )}
              
              {learner.parentGuardianEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.parentGuardianEmail}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* School Information */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <School className="h-5 w-5" />
              School Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                <p className="text-gray-900">{learner.schoolName}</p>
              </div>
              
              {learner.schoolTel && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Telephone</label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.schoolTel}</p>
                  </div>
                </div>
              )}
              
              {learner.schoolEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Email</label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">{learner.schoolEmail}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enrolled Subjects */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrolled Subjects</h3>
            
            {learner.enrolledSubjects && learner.enrolledSubjects.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {learner.enrolledSubjects.map((subject, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full border"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No subjects enrolled</p>
            )}
          </div>
        </div>

        {/* System Information */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            System Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Registered</label>
              <p className="text-gray-900">{formatDate(learner.createdAt)}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
              <p className="text-gray-900">{formatDate(learner.updatedAt)}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-green-600 capitalize">{learner.status}</span>
              </div>
            </div>
            {learner.importBatch && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Import Batch</label>
                <p className="text-gray-900 font-mono text-xs">{learner.importBatch}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnerDetail;